"use client";

import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import clsx from "clsx";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Typography from "src/components/common/Typography/Typography";
import CheckboxField from "src/components/form/CheckboxField/CheckboxField";
import Label from "src/components/form/Label/Label";
import SelectField from "src/components/form/SelectField/SelectField";
import TextField from "src/components/form/TextField/TextField";
import { useExtracted } from "#i18n";
import SchemaTypeChip from "../SchemaTypeChip/SchemaTypeChip";
import styles from "./SchemaTreeView.module.scss";

/**
 * A schema field as SchemaTreeView describes it on the server. Positions (tree ids and form field
 * names) are derived here, so a schema shared by several endpoints is described once.
 */
export interface SchemaTreeField {
  name: string;
  /** The displayed type, e.g. `string (optional)` or `Asset[]`. */
  type?: string;
  kind?: "object" | "array" | "enum";
  isArray?: boolean;
  optional?: boolean;
  /** The Markdown description, rendered on the server. */
  description?: ReactNode;
  enumValues?: string[];
  /** Set for editable scalar fields: the form control the field gets. */
  control?: "checkbox" | "select" | "text" | "number";
  placeholder?: string;
  defaultValue?: string | number | boolean;
}

/** A tree item: a field, one option of a union, or the named type of a root object. */
export type SchemaTreeNode = {
  children?: SchemaTreeNode[];
} /** `expanded`: a union field, which starts open so its options show. */ & (
  | { field: SchemaTreeField; expanded?: boolean }
  | { option: { number: number; summary: string } }
  | { namedType: string; expanded?: boolean }
);

const FieldDescription = (props: { id?: string; field: SchemaTreeField }) => {
  const translateMessage = useExtracted();
  const { id, field } = props;
  const { description, enumValues } = field;
  if (!description && !enumValues?.length) return null;

  return (
    <div id={id} className={styles.description}>
      <div>
        {description}
        {enumValues && enumValues.length > 0 && (
          <div className={styles.enumValues}>
            <Typography as="span" variant="bodyExtraSmall" tone="muted">
              {translateMessage("Allowed values")}
            </Typography>
            <div className={styles.enumList}>
              {enumValues.map((value) => (
                <Typography as="code" variant="code" key={value} className={styles.enumValue}>
                  {value}
                </Typography>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FieldNode = (props: { id: string; path: string; field: SchemaTreeField }) => {
  const { id, path, field } = props;
  const inputId = field.control ? `${id}-input` : undefined;
  const descriptionId = field.description || field.enumValues ? `${id}-description` : undefined;
  const fieldName = (
    <>
      {field.name}
      {field.isArray && "[]"}
    </>
  );

  return (
    <div className={clsx(styles.fieldNode, field.control && styles.editableFieldNode)}>
      <div className={styles.fieldIdentity}>
        <div className={styles.fieldHeading}>
          <Typography
            as={inputId ? "label" : "span"}
            variant="code"
            weight="semibold"
            tone="foreground"
            className={styles.fieldName}
            {...(inputId ? { htmlFor: inputId } : {})}
            id={inputId ? `${inputId}-label` : undefined}
          >
            {fieldName}
            {!field.optional && <Label as="span" required aria-hidden="true" />}
          </Typography>
          {field.type && <SchemaTypeChip size="compact" type={field.type} kind={field.kind} />}
        </div>
        <FieldDescription id={descriptionId} field={field} />
      </div>
      {field.control === "checkbox" ? (
        <CheckboxField
          id={inputId}
          hideLabel
          required={!field.optional}
          name={path}
          defaultValue={field.defaultValue as boolean | undefined}
          className={styles.controlCell}
          // The checkbox is a `span` (the `label` targets its hidden input), so name it explicitly.
          aria-labelledby={`${inputId}-label`}
          aria-describedby={descriptionId}
        />
      ) : field.control === "select" ? (
        <SelectField
          id={inputId}
          hideLabel
          required={!field.optional}
          name={path}
          defaultValue={field.defaultValue}
          className={styles.controlCell}
          controlClassName={styles.select}
          options={(field.enumValues ?? []).map((value) => ({ value, label: value }))}
          placeholder={`Select ${field.name}`}
          aria-describedby={descriptionId}
        />
      ) : field.control ? (
        <TextField
          id={inputId}
          hideLabel
          type={field.control}
          required={!field.optional}
          name={path}
          defaultValue={field.defaultValue}
          placeholder={field.placeholder || `Enter ${field.name}`}
          className={styles.controlCell}
          controlClassName={styles.input}
          aria-describedby={descriptionId}
        />
      ) : null}
    </div>
  );
};

const UnionOptionLabel = (props: { number: number; summary: string }) => {
  const translateMessage = useExtracted();
  const { number, summary } = props;
  return (
    <div className={styles.unionOption}>
      <Label as="span" tone="muted">
        {translateMessage("Option {number}", { number: String(number) })}
      </Label>
      <SchemaTypeChip size="compact" type={summary} />
    </div>
  );
};

/** A tree item with its anchor id and the ids of the items it sits in. */
interface TreeItem {
  /** The item's anchor id (`<tree anchor>.<field>.<field>`, `….option-2` for union options). */
  id: string;
  label: ReactNode;
  children: TreeItem[];
}

interface TreeIndex {
  items: TreeItem[];
  /** Items open on first render: union fields, except those inside a union option. */
  initiallyExpanded: string[];
  /** Each item's ancestors, outermost first, for revealing an item a link points to. */
  ancestors: Map<string, string[]>;
}

/**
 * Tree items with their ids and labels. Ids extend the enclosing field's or option's (the tree's
 * anchor at the root), so each item can be linked to; field names are dotted form paths under the
 * tree's `parentKey`.
 */
const indexTree = (
  nodes: SchemaTreeNode[],
  parentKey: string | undefined,
  scope: string,
): TreeIndex => {
  const initiallyExpanded: string[] = [];
  const ancestors = new Map<string, string[]>();
  const build = (
    nodes: SchemaTreeNode[],
    path: string | undefined,
    scope: string,
    parents: string[],
    inOption: boolean,
  ): TreeItem[] =>
    nodes.map((node) => {
      if ("field" in node) {
        const fieldPath = path ? `${path}.${node.field.name}` : node.field.name;
        const id = `${scope}.${node.field.name}`;
        ancestors.set(id, parents);
        if (node.expanded && node.children?.length && !inOption) initiallyExpanded.push(id);
        return {
          id,
          label: <FieldNode id={id} path={fieldPath} field={node.field} />,
          children: build(node.children ?? [], fieldPath, id, [...parents, id], inOption),
        };
      }
      if ("option" in node) {
        const id = `${scope}.option-${node.option.number}`;
        ancestors.set(id, parents);
        return {
          id,
          label: <UnionOptionLabel {...node.option} />,
          children: build(node.children ?? [], path, id, [...parents, id], true),
        };
      }
      // A root object's named type heads its fields without adding to their ids.
      const id = `${scope}.${node.namedType}`;
      if (node.expanded) initiallyExpanded.push(id);
      ancestors.set(id, parents);
      return {
        id,
        label: (
          <div className={styles.namedType}>
            <SchemaTypeChip size="compact" type={node.namedType} kind="object" />
          </div>
        ),
        children: build(node.children ?? [], path, scope, [...parents, id], inOption),
      };
    });
  return { items: build(nodes, parentKey, scope, [], false), initiallyExpanded, ancestors };
};

const TreeRow = (props: {
  item: TreeItem;
  level: number;
  expanded: boolean;
  onToggle: (id: string) => void;
}) => {
  const translateMessage = useExtracted();
  const { item, level, expanded, onToggle } = props;
  const hasChildren = item.children.length > 0;
  return (
    <div className="relative" style={{ minHeight: "32px" }}>
      {/* biome-ignore lint/a11y/useFocusableInteractive: Items aren't tab stops; their toggles and fields are. */}
      <div
        id={item.id}
        className={clsx(
          "relative flex w-full items-center px-2 gap-2 rounded-md cursor-pointer transition-colors",
          styles.treeNode,
        )}
        style={{ paddingInlineStart: `${level * 1.5 + 0.5}rem`, minWidth: 0 }}
        role="treeitem"
        aria-selected={false}
        aria-expanded={hasChildren ? expanded : undefined}
      >
        <div className="flex items-center gap-2 flex-shrink-0 relative">
          {hasChildren ? (
            <button
              type="button"
              className={clsx(
                "flex w-4 h-4 items-center justify-center transition-colors flex-shrink-0",
                styles.toggle,
              )}
              aria-label={expanded ? translateMessage("Collapse") : translateMessage("Expand")}
              onClick={() => onToggle(item.id)}
            >
              {expanded ? (
                <IconChevronDown size={16} aria-hidden="true" />
              ) : (
                <IconChevronRight size={16} aria-hidden="true" />
              )}
            </button>
          ) : (
            <div className="w-4 h-4" />
          )}
        </div>
        <div className="flex-1 min-w-0 relative">
          <div className={styles.node}>
            <div className={styles.nodeContent}>{item.label}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/** The open items, depth first, with their nesting level. */
const visibleRows = (items: TreeItem[], expanded: ReadonlySet<string>, level = 0) =>
  items.flatMap((item): { item: TreeItem; level: number }[] => [
    { item, level },
    ...(expanded.has(item.id) ? visibleRows(item.children, expanded, level + 1) : []),
  ]);

export interface SchemaTreeProps {
  data: SchemaTreeNode[];
  /** The form value the fields belong to (`query`, `body`), for editable trees. */
  parentKey?: string;
  /** The id items' anchors start with (`<anchor>.<field>`), usually the tree's heading id. */
  anchor?: string;
  className?: string;
  /** Shown above the tree: its title and instructions. */
  children?: ReactNode;
}

/**
 * The expandable tree of a SchemaTreeView. SchemaTreeView walks the schema on the server and sends
 * plain field data, with descriptions already rendered from Markdown; this renders the open items
 * (closed ones render when opened). A link to an item (`#<anchor>.<field>`) opens the items around
 * it and scrolls to it.
 */
const SchemaTree = (props: SchemaTreeProps) => {
  const { data, parentKey, anchor, className, children } = props;
  const generatedId = useId();
  const scope = anchor ?? generatedId;
  const { items, initiallyExpanded, ancestors } = useMemo(
    () => indexTree(data, parentKey, scope),
    [data, parentKey, scope],
  );
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set(initiallyExpanded));
  const [revealed, setRevealed] = useState<string>();
  const scrollRestoreFrameRef = useRef<number | null>(null);

  const toggle = useCallback((id: string) => {
    // Keep the toggle where it was: rows opening or closing must not scroll the page.
    const initialScrollY = window.scrollY;
    if (scrollRestoreFrameRef.current !== null) {
      window.cancelAnimationFrame(scrollRestoreFrameRef.current);
    }
    scrollRestoreFrameRef.current = window.requestAnimationFrame(() => {
      scrollRestoreFrameRef.current = null;
      const scrollOffset = window.scrollY - initialScrollY;
      if (Math.abs(scrollOffset) > 0.5) {
        window.scrollBy(0, -scrollOffset);
      }
    });
    setExpanded((previous) => {
      const next = new Set(previous);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  // Closed items aren't in the document, so the browser can't scroll to them: open the items
  // around a linked one (after hydration, which renders what the server did), then scroll to it.
  useEffect(() => {
    const reveal = () => {
      const id = decodeURIComponent(window.location.hash.slice(1));
      const path = ancestors.get(id);
      if (!path) return;
      setExpanded((previous) =>
        path.every((ancestor) => previous.has(ancestor))
          ? previous
          : new Set([...previous, ...path]),
      );
      setRevealed(id);
    };
    reveal();
    window.addEventListener("hashchange", reveal);
    return () => window.removeEventListener("hashchange", reveal);
  }, [ancestors]);
  useLayoutEffect(() => {
    if (!revealed) return;
    setRevealed(undefined);
    document.getElementById(revealed)?.scrollIntoView({ block: "start" });
  }, [revealed]);

  useEffect(
    () => () => {
      if (scrollRestoreFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollRestoreFrameRef.current);
      }
    },
    [],
  );

  return (
    <div className={clsx(styles.root, className)}>
      {children}
      <div
        className={clsx("bg-white", styles.tree)}
        style={{ height: 400, overflow: "auto" }}
        role="tree"
        aria-multiselectable={false}
        data-anchor-scope={scope}
      >
        <div className="w-full min-w-0">
          {visibleRows(items, expanded).map(({ item, level }) => (
            <TreeRow
              key={item.id}
              item={item}
              level={level}
              expanded={expanded.has(item.id)}
              onToggle={toggle}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SchemaTree;
